<script setup lang="ts">
import { PageEnum } from "@/enums";

// Components
import Dropdown from "@/components/general/Dropdown/Dropdown.vue";
import TextField from "@/components/general/TextField/TextField.vue";
import Button from "@/components/general/Button/Button.vue";
import FindAddress from "@/components/general/FindAddress/FindAddress.vue";

import { useCustomerStore, useDataLayerStore } from "@/stores";
import useVuelidate, { type ErrorObject } from "@vuelidate/core";
import { yourDetailsRules } from "@/validation";
import { AdobeEventTypeEnum, EventTypeEnum } from "@/models";
import JourneyLayout from "@/components/layout/JourneyLayout/JourneyLayout.vue";
import Question from "@/components/general/Question/Question.vue";

// Stores
const customer = useCustomerStore();
const dataLayer = useDataLayerStore();

const emit = defineEmits<{
    submitSection: [currentPage: PageEnum, nextPage: PageEnum];
}>();

const v$ = useVuelidate(yourDetailsRules(customer), customer);

const detailChanged = (key: string) => {
    v$.value[key].$touch();
    if (v$.value[key].$error) {
        dataLayer.errors = v$.value.$errors;
        dataLayer.triggerEvent(EventTypeEnum.ErrorLoad, key);
    }
    dataLayer.triggerEvent(EventTypeEnum.QuestComplete, key);
};

const getErrorMessage = (errors: ErrorObject[] | undefined): string => {
    const message = errors?.[0]?.$message;
    return typeof message === "string" ? message : "";
};

const submitSection = async (currentPage: PageEnum, nextPage: PageEnum) => {
    dataLayer.triggerEvent(
        EventTypeEnum.PageInteraction,
        "button|Payment Details"
    );
    dataLayer.errors = [];
    const isFormCorrect = await v$.value.$validate();
    if (!isFormCorrect) {
        dataLayer.errors = v$.value.$errors;

        dataLayer.triggerEvent(EventTypeEnum.ErrorLoad);

        for (const error of v$.value.$errors) {
            dataLayer.triggerAdobeEvent(
                AdobeEventTypeEnum.FormFieldError,
                error.$propertyPath
            );
        }
        document
            .querySelector(".form-group .error")
            ?.closest(".form-group")
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
    }
    emit("submitSection", currentPage, nextPage);
};
defineExpose({ submitSection });
</script>

<template>
    <JourneyLayout>
        <template #main>
            <div :id="PageEnum.YourDetails" class="your-details-container">
                <div class="your-details-content">
                    <h2>Your Details</h2>
                    <form name="form4" method="post" data-vv-scope="form4">
                        <Question
                            id="your-details-title-question"
                            label="Your title:*"
                            :error-message="getErrorMessage(v$.title?.$errors)"
                            class="cap-width"
                        >
                            <Dropdown
                                id="your-details-title"
                                v-model="customer.title"
                                name="your-details-title"
                                aria-label="Your title"
                                :options="
                                    ['Mr', 'Miss', 'Mrs', 'Ms', 'Dr'].map(
                                        (t) => ({
                                            label: t,
                                            value: t,
                                        })
                                    )
                                "
                                placeholder="Please select"
                                :placeholder-value="''"
                                :has-error="v$.title.$errors.length > 0"
                                :full-width="true"
                                @blur="detailChanged('title')"
                            />
                        </Question>
                        <Question
                            id="your-details-firstname-question"
                            label="Your full first name:*"
                            :error-message="
                                getErrorMessage(v$.firstname?.$errors)
                            "
                            class="cap-width"
                        >
                            <TextField
                                id="your-details-firstname"
                                v-model="customer.firstname"
                                type="text"
                                placeholder="First name"
                                :maxlength="25"
                                :has-error="v$.firstname.$error"
                                @blur="detailChanged('firstname')"
                            />
                        </Question>
                        <!-- MIDDLE NAME-->
                        <Question
                            id="your-details-middlename-question"
                            label="Your middle name:"
                            :error-message="
                                getErrorMessage(v$.middlename?.$errors)
                            "
                            class="cap-width"
                        >
                            <TextField
                                id="your-details-middlename"
                                v-model="customer.middlename"
                                type="text"
                                placeholder="Middle name"
                                :maxlength="25"
                                :has-error="v$.middlename.$error"
                                @blur="detailChanged('middlename')"
                            />
                        </Question>
                        <!-- SURNAME-->
                        <Question
                            id="your-details-surname-question"
                            label="Your surname:*"
                            :error-message="
                                getErrorMessage(v$.surname?.$errors)
                            "
                            class="cap-width"
                        >
                            <TextField
                                id="your-details-surname"
                                v-model="customer.surname"
                                type="text"
                                placeholder="Surname"
                                :maxlength="25"
                                :has-error="v$.surname.$error"
                                @blur="detailChanged('surname')"
                            />
                        </Question>
                        <Question
                            id="your-details-telephone-question"
                            label="Your telephone number:*"
                            :error-message="
                                getErrorMessage(v$.dayPhone?.$errors)
                            "
                            class="cap-width"
                        >
                            <TextField
                                id="your-details-dayPhone"
                                v-model="customer.dayPhone"
                                type="tel"
                                placeholder="Telephone"
                                :maxlength="16"
                                :has-error="v$.dayPhone.$error"
                                @blur="detailChanged('dayPhone')"
                            />
                        </Question>
                        <FindAddress />
                        <Button
                            :id="'submitYourDetails'"
                            btn-type="primary"
                            class="continue"
                            @click.prevent="
                                submitSection(
                                    PageEnum.YourDetails,
                                    PageEnum.PaymentDetails
                                )
                            "
                        >
                            <span>
                                <img
                                    class="padlock"
                                    src="@img/secure-padlock-icon-white.svg"
                                    alt="Secure"
                                />
                            </span>
                            Payment Details
                        </Button>
                    </form>
                </div>

                <img
                    class="hundred-percent"
                    src="@img/100-percent-claims.svg"
                    alt="100% of claims paid, most within a day"
                />
            </div>
        </template>
    </JourneyLayout>
</template>

<style scoped>
.your-details-container {
    display: flex;
    justify-content: space-between;
    padding-top: 0.625rem;
}

.your-details-content {
    min-width: 51.5rem;
}

.hundred-percent {
    max-width: 16rem;
}

.form-group {
    margin-bottom: 0;
    padding-bottom: 15px;
    background: #efefef;
}

fieldset .question-error {
    color: blue;
}

.text-field {
    width: 100%;
}

.cap-width {
    max-width: 33.75rem;
}

.padlock {
    height: 1.375rem;
    vertical-align: text-bottom;
    margin-right: 0.25rem;
}

@media screen and (width <= 585px) {
    .your-details-container {
        padding-right: 15px;
        flex-direction: column;
        align-items: center;
    }

    .your-details-content {
        min-width: calc(100% - 2rem);
    }
}
</style>
